import React, { useState } from 'react';
import { Alert, Box, CircularProgress, Collapse, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../constants/documentTheme';
import { PARTNER_BOTTOM_NAV_HEIGHT, PARTNER_SIDEBAR_WIDTH, partnerWorkspaceViewportSx } from '../utils/partnerWorkspace';
import PartnerWorkspaceFooter from './PartnerWorkspaceFooter';

export const partnerPageWrapSx = {
  bgcolor: DOCUMENT.canvas,
  minHeight: '100vh',
  color: DOCUMENT.ink,
  fontFamily: DOCUMENT.sans,
};

export const partnerPageInnerSx = {
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  overflowX: 'hidden',
  // Explicit pl/pr (not px+pl) so sidebar offset cannot wipe mobile left padding.
  pl: {
    xs: 2,
    sm: 3,
    md: `calc(${PARTNER_SIDEBAR_WIDTH}px + 40px)`,
  },
  pr: {
    xs: 2,
    sm: 3,
    md: 5,
  },
  pb: {
    xs: `calc(${PARTNER_BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 24px)`,
    md: 8.75,
  },
};

export const partnerOutlineBtnSx = {
  background: 'transparent',
  color: DOCUMENT.oxblood,
  border: `1px solid ${DOCUMENT.oxblood}`,
  borderRadius: 0,
  font: 'inherit',
  fontSize: 11,
  letterSpacing: '0.11em',
  textTransform: 'uppercase',
  lineHeight: 1.25,
  px: 2.5,
  py: 1.35,
  minWidth: 0,
  minHeight: 40,
  boxSizing: 'border-box',
  boxShadow: 'none',
  '&:hover': {
    background: `color-mix(in srgb, ${DOCUMENT.oxblood} 8%, transparent)`,
    boxShadow: 'none',
  },
};

export const partnerOxbloodBtnSx = {
  background: DOCUMENT.oxblood,
  color: DOCUMENT.ivory,
  // Same 1px border as outline buttons so filled/outline stay equal height.
  border: `1px solid ${DOCUMENT.oxblood}`,
  borderRadius: 0,
  font: 'inherit',
  fontSize: 11,
  letterSpacing: '0.11em',
  textTransform: 'uppercase',
  lineHeight: 1.25,
  px: 2.5,
  py: 1.35,
  minWidth: 0,
  minHeight: 40,
  boxSizing: 'border-box',
  boxShadow: 'none',
  '&:hover': {
    background: DOCUMENT.nav,
    borderColor: DOCUMENT.nav,
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    background: DOCUMENT.ruleHard,
    borderColor: DOCUMENT.ruleHard,
    color: DOCUMENT.ink3,
  },
};

export const partnerLinkSx = {
  background: 'none',
  border: 0,
  p: 0,
  pb: '2px',
  minWidth: 0,
  font: 'inherit',
  fontSize: 11,
  letterSpacing: '0.11em',
  textTransform: 'uppercase',
  color: DOCUMENT.ink2,
  borderBottom: '1px solid transparent',
  borderRadius: 0,
  boxShadow: 'none',
  cursor: 'pointer',
  '&:hover': {
    background: 'none',
    color: DOCUMENT.ink,
    borderBottomColor: DOCUMENT.accent,
    boxShadow: 'none',
  },
};

export const partnerDangerLinkSx = {
  ...partnerLinkSx,
  '&:hover': {
    background: 'none',
    color: '#8C2B22',
    borderBottomColor: '#8C2B22',
    boxShadow: 'none',
  },
};

export const PartnerPageShell = ({
  children,
  error,
  onClearError,
  success,
  onClearSuccess,
  fullPage = false,
}) => (
  <Box
    data-testid="partner-page-shell"
    data-full-page={fullPage ? 'true' : undefined}
    sx={{
      ...partnerPageWrapSx,
      ...(fullPage ? {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: partnerWorkspaceViewportSx.height,
        maxHeight: partnerWorkspaceViewportSx.maxHeight,
        overflow: 'hidden',
      } : {}),
    }}
  >
    <Box
      sx={{
        ...partnerPageInnerSx,
        ...(fullPage ? {
          width: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          pb: 0,
          overflow: 'hidden',
        } : {}),
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mt: 2, flexShrink: 0 }} onClose={onClearError}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mt: 2, flexShrink: 0 }} onClose={onClearSuccess}>{success}</Alert>
      )}
      {children}
      <PartnerWorkspaceFooter sx={fullPage ? { mt: 1.5, pt: 1.5, pb: 1.5, flexShrink: 0 } : undefined} />
    </Box>
  </Box>
);

export const PartnerPageHeader = ({ title, subtitle, actions, compact = false }) => (
  <Box sx={{ pt: compact ? { xs: 2, md: 2.25 } : { xs: 4.5, md: 4.5 }, flexShrink: 0 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 2.25,
        flexWrap: 'wrap',
        pb: 1.75,
        borderBottom: `1px solid ${DOCUMENT.ink}`,
      }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: DOCUMENT.display,
          fontSize: { xs: 28, md: 40 },
          fontWeight: 400,
          lineHeight: 1,
          color: DOCUMENT.ink,
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, pb: 0.6 }}>
          {subtitle}
        </Typography>
      )}
      {actions && (
        <Box sx={{ ml: { sm: 'auto' }, display: 'flex', gap: 2, alignItems: 'center', pb: 0.5, width: { xs: '100%', sm: 'auto' } }}>
          {actions}
        </Box>
      )}
    </Box>
  </Box>
);

export const PartnerFilterBar = ({
  chips = [],
  activeChip,
  onChipChange,
  search,
  onSearchChange,
  searchPlaceholder,
  sort,
  onSortChange,
  sortOptions = [],
  bordered = true,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: { xs: 1, md: '8px 22px' },
      flexWrap: 'wrap',
      py: bordered ? 2 : 0,
      borderBottom: bordered ? `1px solid ${DOCUMENT.rule}` : 0,
      flexShrink: 0,
      width: bordered ? undefined : '100%',
    }}
  >
    {chips.length > 0 && (
    <Box sx={{ display: 'flex', gap: 0.9, flexWrap: 'wrap' }}>
      {chips.map((chip) => {
        const pressed = chip.id === activeChip;
        return (
          <Box
            key={chip.id}
            component="button"
            type="button"
            aria-pressed={pressed}
            onClick={() => onChipChange(chip.id)}
            sx={{
              background: pressed ? DOCUMENT.oxblood : 'none',
              border: `1px solid ${pressed ? DOCUMENT.oxblood : DOCUMENT.ruleHard}`,
              color: pressed ? DOCUMENT.ivory : DOCUMENT.ink2,
              font: 'inherit',
              fontSize: 12,
              px: 1.6,
              py: 0.75,
              cursor: 'pointer',
            }}
          >
            {chip.label}
            {chip.count != null && (
              <Box
                component="span"
                sx={{
                  ml: 0.75,
                  color: pressed ? '#D6BE93' : DOCUMENT.ink3,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {chip.count}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
    )}
    {onSearchChange && (
      <Box
        sx={{
          ml: { md: 'auto' },
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          minWidth: { xs: '100%', md: 300 },
          width: { md: 300 },
          flex: { xs: '1 1 100%', md: '0 0 300px' },
        }}
      >
        <Box
          component="input"
          type="search"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          sx={{
            background: 'transparent',
            border: 0,
            borderBottom: `1px solid ${DOCUMENT.ruleHard}`,
            font: 'inherit',
            fontSize: 14,
            pt: 0.6,
            pb: 0.9,
            width: '100%',
            color: DOCUMENT.ink,
            outline: 0,
            '&:focus': { borderBottomColor: DOCUMENT.accent },
            '&::placeholder': { color: DOCUMENT.ink3, opacity: 1 },
          }}
        />
      </Box>
    )}
    {sortOptions.length > 0 && (
      <Box
        component="select"
        value={sort}
        onChange={(event) => onSortChange(event.target.value)}
          aria-label="Sort"
          sx={{
            background: 'transparent',
            border: 0,
            font: 'inherit',
            fontSize: 12,
            color: DOCUMENT.ink2,
            cursor: 'pointer',
            outline: 0,
          }}
        >
          {sortOptions.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </Box>
    )}
  </Box>
);

export const PartnerEmptyState = ({ title, body, action }) => (
  <Box sx={{ py: 8, px: 3.75, textAlign: 'center' }}>
    <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 24, fontWeight: 400, mb: 1.1, color: DOCUMENT.ink }}>
      {title}
    </Typography>
    {body && (
      <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2, maxWidth: '42ch', mx: 'auto', mb: action ? 2.25 : 0, lineHeight: 1.65 }}>
        {body}
      </Typography>
    )}
    {action}
  </Box>
);

export const PartnerNote = ({ children }) => (
  <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, pt: 2.25, lineHeight: 1.6 }}>
    {children}
  </Typography>
);

export const PartnerLoading = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
    <CircularProgress size={28} sx={{ color: DOCUMENT.oxblood }} />
  </Box>
);

export const PartnerTripHero = ({
  title,
  description,
  badge,
  coverSrc,
  dates,
  children,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
  <Box
    data-testid="partner-trip-hero"
    sx={{
      position: 'relative',
      overflow: 'hidden',
      bgcolor: DOCUMENT.oxblood,
      color: DOCUMENT.ivory,
      px: { xs: 2.5, md: 4 },
      py: expanded ? { xs: 3, md: 4 } : { xs: 2, md: 2.5 },
      mt: 2.5,
      minHeight: coverSrc ? (expanded ? { xs: 200, md: 240 } : { xs: 140, md: 160 }) : undefined,
      backgroundImage: coverSrc ? `url(${coverSrc})` : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      ...(coverSrc ? {
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          // Keep the photo visible; darken mainly under the text column.
          background: [
            'linear-gradient(105deg, rgba(36,19,24,0.72) 0%, rgba(36,19,24,0.42) 48%, rgba(36,19,24,0.18) 100%)',
            'linear-gradient(180deg, rgba(36,19,24,0.18) 0%, rgba(36,19,24,0.36) 100%)',
          ].join(', '),
          pointerEvents: 'none',
        },
      } : {}),
    }}
  >
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      {badge && (
        <Typography
          sx={{
            fontSize: 10,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: DOCUMENT.accent,
            mb: 1,
          }}
        >
          {badge}
        </Typography>
      )}
      <Typography
        component="h1"
        sx={{
          fontFamily: DOCUMENT.display,
          fontSize: { xs: 32, md: 44 },
          fontWeight: 400,
          lineHeight: 1.08,
          color: DOCUMENT.ivory,
          textShadow: coverSrc ? '0 1px 10px rgba(36,19,24,0.35)' : 'none',
        }}
      >
        {title}
      </Typography>
      {dates && (
        <Typography
          data-testid="partner-trip-hero-dates"
          sx={{ mt: 0.5, fontSize: { xs: 13, sm: 14 }, color: 'rgba(246,239,228,0.82)' }}
        >
          {dates}
        </Typography>
      )}
      <Collapse in={expanded} timeout={250}>
        {description && (
          <Typography sx={{ mt: 1.25, fontSize: 14, color: 'rgba(246,239,228,0.92)', maxWidth: 560 }}>
            {description}
          </Typography>
        )}
        {children}
      </Collapse>
      <Box
        component="button"
        type="button"
        data-testid="partner-trip-hero-toggle"
        onClick={() => setExpanded((value) => !value)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mt: 1,
          p: 0,
          width: '100%',
          border: 0,
          bgcolor: 'transparent',
          color: 'rgba(246,239,228,0.75)',
          cursor: 'pointer',
          font: 'inherit',
        }}
      >
        <Typography sx={{ fontSize: 11, letterSpacing: '0.06em', mr: 0.5 }}>
          {expanded ? t('common.showLess') : t('trip.showDetails')}
        </Typography>
        {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
      </Box>
    </Box>
  </Box>
  );
};
